<script setup>
import { ref, computed, watch, inject } from 'vue'

const props = defineProps({ 
  currentStep: { type: Object, required: true },
  reminderData: { type: Object, default: () => ({ items: [] }) },
  expandAll: { type: Boolean, default: null }
})

const emit = defineEmits(['go-to-step', 'expanded-state-changed'])
const emitter = inject('emitter', null)

// Handle requirement click to open offcanvas with definition
function onRequirementClicked(req) {
  const firstEmp = req.employers && req.employers.length ? req.employers[0] : null
  if (!firstEmp || !emitter) return
  emitter.emit('action-center:event-clicked', {
    event: { 
      id: firstEmp.eventId || null, 
      name: req.requirementName, 
      body: req.eventBody, 
      date: req.eventMonthDay, 
      dueDate: req.earliestDueDateObj 
    },
    employer: { 
      name: firstEmp.employerName, 
      plan_start_date: firstEmp.plan_start_date, 
      id: firstEmp.employerId 
    },
    source: 'action-center'
  })
}

// Expandable sections state
const expandedSections = ref({
  requirements: false,
  employers: false,
  recipients: false
})

// Toggle individual section
function toggleSection(section) {
  expandedSections.value[section] = !expandedSections.value[section]
  emitExpandedState()
}

// Expand/Collapse all
const allExpanded = computed(() => 
  expandedSections.value.requirements && 
  expandedSections.value.employers && 
  expandedSections.value.recipients
)

// Watch for parent's expand/collapse trigger
watch(() => props.expandAll, (newVal) => {
  if (newVal !== null) {
    expandedSections.value.requirements = newVal
    expandedSections.value.employers = newVal
    expandedSections.value.recipients = newVal
    emitExpandedState()
  }
})

function emitExpandedState() {
  emit('expanded-state-changed', allExpanded.value)
}

// Computed data - return full items for click functionality
const requirementsList = computed(() => props.reminderData.items)

const employersList = computed(() => {
  const map = new Map()
  for (const req of props.reminderData.items) {
    for (const emp of req.employers) {
      if (!map.has(emp.employerId)) {
        map.set(emp.employerId, emp.employerName)
      }
    }
  }
  return Array.from(map.values()).sort((a, b) => a.localeCompare(b))
})

// Count total recipients (employer + email combinations that will be sent)
const totalRecipients = computed(() => {
  const seen = new Set()
  for (const req of props.reminderData.items) {
    for (const emp of req.employers) {
      for (const email of (emp.selectedEmails || [])) {
        // Each employer+email combo counts as 1 recipient
        seen.add(`${emp.employerId}::${email}`)
      }
    }
  }
  return seen.size
})

// Get recipients grouped by employer (for display in expanded section)
const recipientsByEmployer = computed(() => {
  const map = new Map()
  for (const req of props.reminderData.items) {
    for (const emp of req.employers) {
      if (!map.has(emp.employerId)) {
        map.set(emp.employerId, {
          employerId: emp.employerId,
          employerName: emp.employerName,
          emails: new Set(emp.selectedEmails || [])
        })
      } else {
        // Merge emails if employer appears in multiple requirements
        const existing = map.get(emp.employerId)
        for (const email of (emp.selectedEmails || [])) {
          existing.emails.add(email)
        }
      }
    }
  }
  return Array.from(map.values())
    .map(e => ({ ...e, emails: Array.from(e.emails).sort() }))
    .sort((a, b) => a.employerName.localeCompare(b.employerName))
})

// Navigation handler
function goToStep(step) {
  emit('go-to-step', step)
}
</script>

<template>
  <div id="step3SummaryContainer" class="mb-4">
    <!-- Summary table -->
    <div id="step3SummaryTable" class="summary-table">
      <!-- Requirements row -->
      <div id="step3RequirementsRow" class="summary-row">
        <div id="step3RequirementsHeader" class="summary-row-header" @click="toggleSection('requirements')">
          <div class="row-label header-20"><strong>Requirements:</strong></div>
          <div class="row-value header-20 text-primary">
            <strong>{{ reminderData.items.length }}</strong>
            <span class="ms-2">requirements selected</span>
          </div>
          <div class="row-toggle">
            <i class="bi" :class="expandedSections.requirements ? 'bi-caret-up-fill' : 'bi-caret-down-fill'"></i>
          </div>
        </div>
        <div v-if="expandedSections.requirements" class="summary-row-content">
          <div class="content-spacer"></div>
          <div class="content-grid  pt-3">
            <div class="content-items">
              <div 
                v-for="req in requirementsList" 
                :key="req.key" 
                class="content-item text-primary text-16"
                role="button"
                @click.stop="onRequirementClicked(req)"
              >
                <strong>{{ req.requirementName }}</strong>
              </div>
            </div>
            <div class="content-action">
              <button id="step3EditRequirementsBtn" type="button" class="btn btn-primary btn-sm text-14" @click.stop="goToStep(0)">
                <i class="bi bi-pencil-square me-1"></i><med>Edit Requirements</med>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Employers row -->
      <div id="step3EmployersRow" class="summary-row">
        <div id="step3EmployersHeader" class="summary-row-header" @click="toggleSection('employers')">
          <div class="row-label header-20"><strong>Employers:</strong></div>
          <div class="row-value header-20 text-primary">
            <strong>{{ employersList.length }}</strong>
            <span class="ms-2">employers selected</span>
          </div>
          <div class="row-toggle">
            <i class="bi" :class="expandedSections.employers ? 'bi-caret-up-fill' : 'bi-caret-down-fill'"></i>
          </div>
        </div>
        <div v-if="expandedSections.employers" class="summary-row-content">
          <div class="content-spacer"></div>
          <div class="content-grid pt-3">
            <div class="content-items employers-grid">
              <div v-for="emp in employersList" :key="emp" class="content-item text-16">
                {{ emp }}
              </div>
            </div>
            <div class="content-action">
              <button id="step3EditEmployersBtn" type="button" class="btn btn-primary btn-sm text-14" @click.stop="goToStep(0)">
                <i class="bi bi-pencil-square me-1"></i><med>Edit Employers</med>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Recipients row -->
      <div id="step3RecipientsRow" class="summary-row">
        <div id="step3RecipientsHeader" class="summary-row-header" @click="toggleSection('recipients')">
          <div class="row-label header-20"><strong>Recipients:</strong></div>
          <div class="row-value header-20 text-primary">
            <strong>{{ totalRecipients }}</strong>
            <span class="ms-2">recipients selected</span>
          </div>
          <div class="row-toggle">
            <i class="bi" :class="expandedSections.recipients ? 'bi-caret-up-fill' : 'bi-caret-down-fill'"></i>
          </div>
        </div>
        <div v-if="expandedSections.recipients" class="summary-row-content">
          <div class="content-spacer"></div>
          <div class="content-grid pt-3">
            <div class="content-items recipients-by-employer">
              <div v-for="emp in recipientsByEmployer" :key="emp.employerId" class="employer-emails-group">
                <div class="text-16"><strong>{{ emp.employerName }}</strong></div>
                <div v-for="email in emp.emails" :key="email" class="text-16">
                  {{ email }}
                </div>
              </div>
            </div>
            <div class="content-action">
              <button id="step3EditRecipientsBtn" type="button" class="btn btn-primary btn-sm text-14" @click.stop="goToStep(1)">
                <i class="bi bi-pencil-square me-1"></i><med>Edit Recipients</med>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- From row (static, not expandable) -->
      <div id="step3FromRow" class="summary-row static">
        <div class="summary-row-header">
          <div class="row-label header-20"><strong>From:</strong></div>
          <div class="row-value header-20">
            <span>Reminders@BCSPro.com (This is a no-reply account)</span>
          </div>
          <div class="row-toggle"></div>
        </div>
      </div>

      <!-- Message row (static, not expandable) -->
      <div id="step3MessageRow" class="summary-row static">
        <div class="summary-row-header">
          <div class="row-label header-20"><strong>Message:</strong></div>
          <div class="row-value header-20">
            <span>Each reminder will show a title and message, which includes the requirement name, description, and due date.</span>
          </div>
          <div class="row-toggle"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.summary-table {
  border: 1px solid var(--bs-border-color);
  border-radius: 4px;
  overflow: hidden;
}

.summary-row {
  border-bottom: 1px solid var(--bs-border-color);
  display: flex;
  flex-wrap: wrap;
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-row-header {
  display: flex;
  align-items: center;
  width: 100%;
  cursor: pointer;  
}

.summary-row.static .summary-row-header {
  cursor: default;
}

/* Label column - gray background */
.row-label {
  width: 200px;
  flex-shrink: 0;
  padding: 28px 20px;
  background-color: var(--bs-gray-100);
  align-self: stretch;
  display: flex;
  align-items: center;
}

/* Value column - white background */
.row-value {
  flex: 1;
  font-size: 16px;
  padding: 16px 20px;
  background-color: #fff;
}

/* Toggle icon column */
.row-toggle {
  width: 50px;
  text-align: center;
  color: var(--bs-primary);
  padding: 16px 10px;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Expanded content area */
.summary-row-content {
  width: 100%;
  display: flex;
  background-color: #fff;
  border-top: 1px solid var(--bs-border-color);
}

/* Empty spacer to align with label column */
.content-spacer {
  width: 200px;
  flex-shrink: 0;
  background-color: var(--bs-gray-100);
}

.content-grid {
  flex: 1;
  display: flex;
  gap: 20px;
  padding: 0 20px 20px 20px;
}

.content-items {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.content-items.employers-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px 24px;
}

/* Recipients grouped by employer - 3 column layout */
.content-items.recipients-by-employer {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px 24px;
}

.employer-emails-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.content-item {
  font-size: 14px;
}

.content-action {
  flex-shrink: 0;
  align-self: flex-start;
}
</style>
